import type { FunctionComponent } from 'react';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useTheme } from 'styled-components';
import { DefaultTheme } from 'styled-components/dist/types';
import { useDynamicYAxisWidth } from './hooks/useDynamicYAxisWidth';
import { TimeseriesAreaGraphTooltip } from './subcomponents/TimeseriesAreaGraphTooltip';

const ResponsiveContainerHeight = 150;
const xAxisTickMargin = 8;
const xAxisTickSize = 6;
const xAxisTickFontSize = 12;
const yAxisTickMargin = 12;
const yAxisTickCount = 4;
const yAxisTickFontSize = 12;
const areaStrokeWidth = 2.5;
const activeDotStrokeWidth = 2.5;
const activeDotRadius = 3.5;
const dotLineStrokeWidth = 2.3;

export const mapGraphVariantToColors = (
  variant: GraphVariant,
  theme: DefaultTheme,
): { lightColor: string; darkColor: string } => {
  switch (variant) {
    case 'GREEN':
      return {
        lightColor: theme.palette.graphicGreen,
        darkColor: theme.palette.graphicGreen,
      };
    case 'LIGHT_GREEN':
    case 'GREEN_SECONDARY':
      return {
        lightColor: theme.palette.textGreen,
        darkColor: theme.palette.textGreen,
      };
    case 'ORANGE':
      return {
        lightColor: theme.palette.graphicOrange,
        darkColor: theme.palette.graphicOrange,
      };
    case 'RED':
      return {
        lightColor: theme.palette.graphicPink,
        darkColor: theme.palette.graphicPink,
      };
    case 'BLUE':
      return {
        lightColor: theme.palette.graphicBlue,
        darkColor: theme.palette.graphicBlue,
      };
    case 'PURPLE':
      return {
        lightColor: theme.palette.graphicPurple,
        darkColor: theme.palette.graphicPurple,
      };
    case 'DARK_PURPLE':
      return {
        lightColor: theme.palette.borderBrandStatic,
        darkColor: theme.palette.borderBrandStatic,
      };
  }
};

interface GraphPointHover {
  label: string;
  title?: string | null;
  subtitle: string;
}

export interface TimeSeriesAreaGraphPoint {
  date: string; // e.g. 2018-03-31
  value?: number | null;
  hover?: GraphPointHover | null;
}

export type GraphVariant =
  | 'BLUE'
  | 'GREEN'
  | 'GREEN_SECONDARY'
  | 'ORANGE'
  | 'PURPLE'
  | 'DARK_PURPLE'
  | 'RED'
  | 'LIGHT_GREEN';

export interface TimeSeriesAreaGraph {
  points: TimeSeriesAreaGraphPoint[];
  variant: GraphVariant;
}

interface TimeseriesAreaGraphProps {
  title: string;
  graph: TimeSeriesAreaGraph;
  graphId?: string;
  formatXAxisValue?: (value: string) => string;
  formatValueFn?: (value: number) => string;
  getXAxisTicks: (points: TimeSeriesAreaGraphPoint[]) => string[];
  boldHoverTitle?: boolean;
  yDomainOverride?: [number, number];
  className?: string;
  tooltipDisabled?: boolean;
  preserveStart?: boolean;
}

export const TimeseriesAreaGraph: FunctionComponent<TimeseriesAreaGraphProps> = ({
  title,
  graph: { points, variant },
  graphId,
  formatXAxisValue,
  formatValueFn,
  getXAxisTicks,
  className,
  yDomainOverride,
  boldHoverTitle = true,
  preserveStart = false,
  tooltipDisabled = false,
}) => {
  const theme = useTheme();
  const { darkColor, lightColor } = mapGraphVariantToColors(variant, theme);
  const { yAxisWidth, setChartRef } = useDynamicYAxisWidth();

  return (
    <ResponsiveContainer
      className={className}
      aria-label={title}
      width="100%"
      height={ResponsiveContainerHeight}
    >
      <ComposedChart
        data={points}
        ref={setChartRef}
        margin={{ top: 20, right: 6, left: 6, bottom: 0 }}
      >
        <defs>
          <linearGradient id={graphId} x1=".5" y1="1" x2=".5">
            <stop stopColor={lightColor} stopOpacity="0" />
            <stop stopColor={lightColor} offset="0.9" />
          </linearGradient>
        </defs>
        <CartesianGrid
          strokeDasharray="6 4"
          vertical={false}
          stroke={theme.palette.borderSecondary}
        />
        {!tooltipDisabled && (
          <RTooltip
            filterNull={true}
            content={<TimeseriesAreaGraphTooltip boldHoverTitle={boldHoverTitle} />}
            wrapperStyle={{ outline: 'none' }}
          />
        )}
        <XAxis
          dataKey="date"
          ticks={getXAxisTicks(points)}
          tickMargin={xAxisTickMargin}
          tickSize={xAxisTickSize}
          interval={preserveStart ? 'preserveStart' : undefined}
          domain={['auto', 'auto']}
          minTickGap={24}
          tickFormatter={formatXAxisValue}
          axisLine={{ stroke: theme.palette.borderPrimary }}
          tickLine={{ stroke: theme.palette.borderPrimary }}
          label={{ stroke: theme.palette.textSecondary }}
          tick={{ fontSize: xAxisTickFontSize }}
        />
        <YAxis
          type="number"
          domain={yDomainOverride ?? ['auto', 'auto']}
          tickLine={false}
          tickMargin={yAxisTickMargin}
          axisLine={false}
          width={yAxisWidth + yAxisTickMargin}
          tickCount={yAxisTickCount}
          tickFormatter={formatValueFn}
          label={{ stroke: theme.palette.textSecondary }}
          tick={{ fontSize: yAxisTickFontSize }}
        />
        <Line
          dataKey="value"
          type="monotone"
          stroke={darkColor}
          strokeDasharray="4 2"
          strokeWidth={dotLineStrokeWidth}
          connectNulls
          activeDot={false}
          dot={false}
        />
        <Area
          dataKey="value"
          type="monotone"
          stroke={darkColor}
          strokeWidth={areaStrokeWidth}
          fill={`url(#${graphId})`}
          activeDot={{
            stroke: darkColor,
            strokeWidth: activeDotStrokeWidth,
            fill: '#FFFFFF',
            r: activeDotRadius,
          }}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
};
